import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { bodyTooltip, chartTitle, type OptionContext } from './shared.ts';

export function buildScatterOption(
  config: Extract<ChartConfig, { type: 'scatter' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const pts = config.points;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs, minX + 1e-6);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys, minY + 1e-6);
  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({
      trigger: 'item',
      formatter: (p) => {
        const item = p as { value: number[]; name?: string };
        const v = item.value;
        return `${item.name ?? 'Point'}: (${v[0]}, ${v[1]})`;
      },
    }),
    grid: {
      left: 8,
      right: 8,
      top: config.title ? 36 : 8,
      bottom: 8,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      min: minX,
      max: maxX,
      name: config.xLabel,
      nameTextStyle: { color: textColor, fontSize: 10 },
      axisLabel: { color: textColor, fontSize: 10 },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    yAxis: {
      type: 'value',
      min: minY,
      max: maxY,
      name: config.yLabel,
      nameTextStyle: { color: textColor, fontSize: 10 },
      axisLabel: { color: textColor, fontSize: 10 },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    series: [
      {
        type: 'scatter',
        data: pts.map((p) => ({
          value: [p.x, p.y],
          name: p.name,
        })),
        symbolSize: 9,
        itemStyle: { color: colors.primary, opacity: 0.85 },
      },
    ],
  };
}
